import { IControlDefinition } from "Grids/Interfaces";
import { ITextControl, ILength } from "Controls/Interfaces";
import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { TextBlockService } from "Service";
import { Util } from "Core";

/**
 * ## Text
 * 
 * Control for text input.
 * @since 4.0.0
 * @example 
 * ```JavaScript
 * // Access the instance of an TextControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 * 
 * // On page:
 * $dms(1337)
 * ```
 */
export class TextControl extends Control implements ITextControl, ILength {

    /**
     * The binding property for the default binding. (Default: value).
     * @hidden
     */
    protected bindingProperty: string = "valueNull";

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-text";

    /**
     * Create a new TextControl.
     * @param controlDefinition ControlDefinition.
     * @hidden
     */
    constructor(controlDefinition: IControlDefinition, textBlockCategory: string, textBlockService: TextBlockService) {
        super(controlDefinition, textBlockCategory, textBlockService);
        if (!controlDefinition.Height) {
            this.defaultEditorElement = "<input class='k-textbox'/>";
        } else {
            this.defaultEditorElement = "<textarea class='k-textbox'/>";
        }
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("change", (e) => this.trigger(ControlEventTypes.Change));
        this.jQueryElement.on("focusout", (e) => {
            if (this.controlDefinition.Trim) {
                this.setValue(Util.trim(this.getValue(), " "));
            }
            this.trigger(ControlEventTypes.LostFocus);
        });
        if (this.controlDefinition.IsPassword) {
            this.jQueryElement.attr("type", "Password");
        }
        if (this.controlDefinition.MaxLength) {
            this.jQueryElement.attr("maxlength", this.controlDefinition.MaxLength);
        }
        
        this.jQueryElement.on("focus", () => {
            setTimeout(() => this.jQueryElement.select());
        });
    }

    /**
     * Gets the display template for readonly columns and non-editable grid
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        if (this.controlDefinition.IsPassword) {
            return "******";
        } else {
            if (this.controlDefinition.EncodeHtml) {
                return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue || ""}" : DMS.Util.encode(DMS.Util.convertToString(${bindingField}), DMS.Enums.EncodingTypes.Html)#`;
            } else {
                return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue || ""}" : DMS.Util.convertToString(${bindingField})#`;
            }
        }
    }

    /**
     * Get the text value.
     * @since 4.0.0
     */
    public getValue(): string {
        return Util.stringIsNullOrEmpty(this.jQueryElement.val() as string) ? null : this.jQueryElement.val() as string;
    }

    /**
     * Set the text.
     * @param value Text to be set.
     * @since 4.0.0
     */
    public setValue(value: string) {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this.jQueryElement.val(value);
            this.jQueryElement.change();
        }
    }

    /**
     * Set the maximum input length.
     * @param maxlength Number of allowed characters.
     * @since 4.0.0
     */
    public setMaxLength(maxlength: number): void {
        this.jQueryElement.prop("maxlength", maxlength);
    }

    /**
     * Get the maximum input length.
     * @since 4.0.0
     */
    public getMaxLength(): number {
        return this.jQueryElement.prop("maxlength");
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this.jQueryElement) {
            this.jQueryElement.off("change");
            this.jQueryElement.off("focusout");
        }
    }
}
